import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Star,
  ShieldCheck,
  Video,
  Building,
  Home,
  FileText,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  ArrowRight,
  MapPin,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { schedulingApi, filesApi } from '@/api';
import { cn } from '@/utils/cn';

interface InAppDoctorCardProps {
  doctor: any;
  currentModality?: 'TELEMEDICINA' | 'PRESENCIAL' | 'DOMICILIAR';
  formattedPrice: string;
  onSelectDoctor: (doctor: any) => void;
  onSelectSlot: (doctor: any, dateStr: string, slotTime: string, slotId: string) => void;
}

export function InAppDoctorCard({
  doctor,
  formattedPrice,
  onSelectDoctor,
  onSelectSlot,
}: InAppDoctorCardProps) {
  const [dayOffset, setDayOffset] = useState(0);
  const [imgError, setImgError] = useState(false);

  const displayName = doctor.profile?.displayName || doctor.name || 'Dr(a). Especialista';
  const specialty = doctor.primarySpecialty?.name || doctor.specialty?.name || doctor.specialty || 'Clínica Geral';
  const realCrm = doctor.crm
    ? (doctor.crm.startsWith('CRM') ? `${doctor.crm}/${doctor.crmState || 'SP'}` : `CRM ${doctor.crm}/${doctor.crmState || 'SP'}`)
    : 'CRM Ativo';
  const rawPhoto = doctor.profile?.photoUrl || doctor.photoUrl;
  const photoUrl = rawPhoto
    ? rawPhoto.startsWith('http') || rawPhoto.startsWith('data:')
      ? rawPhoto
      : filesApi.getFileUrl(rawPhoto)
    : null;

  const isOnline = Boolean(doctor.isOnline || doctor.onlineStatus === 'ONLINE');
  const rating = doctor.statistics?.averageRating || 4.9;
  const totalRatings = doctor.statistics?.totalRatings || 148;
  const bio = doctor.profile?.bio || 'Cada paciente é único e merece atenção integral.';

  // Fuso Horário de São Paulo
  const getSaoPauloDateStr = (d: Date = new Date()): string => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  };

  const getSaoPauloTimeStr = (d: Date = new Date()): string => {
    return new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  };

  const spTodayStr = useMemo(() => getSaoPauloDateStr(), []);
  const nowSpTimeStr = useMemo(() => getSaoPauloTimeStr(), []);

  // Intervalo de datas para a query de disponibilidade
  const dateRange = useMemo(() => {
    const from = new Date();
    from.setDate(from.getDate() + dayOffset);
    const to = new Date();
    to.setDate(to.getDate() + dayOffset + 3);

    const fromIso = from.toISOString().split('T')[0] ?? '';
    const toIso = to.toISOString().split('T')[0] ?? '';
    return { fromIso, toIso };
  }, [dayOffset]);

  // Consulta disponibilidade de slots do médico
  const { data: realCalendar } = useQuery({
    queryKey: ['scheduling', 'doctor-calendar', doctor.id, dateRange.fromIso, dateRange.toIso],
    queryFn: () => schedulingApi.getAvailabilityCalendar(doctor.id, dateRange.fromIso, dateRange.toIso),
    enabled: Boolean(doctor.id),
    staleTime: 1000 * 60 * 5,
  });

  // Montagem dos 3 dias Doctoralia com horários interativos
  const threeDaysSchedule = useMemo(() => {
    const days: Array<{
      dayLabel: string;
      dateStr: string;
      formatShort: string;
      slots: string[];
    }> = [];

    const weekDayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    for (let i = 0; i < 3; i++) {
      const dayIndex = dayOffset + i;
      const targetDate = new Date();
      targetDate.setDate(targetDate.getDate() + dayIndex);

      const targetDateKey = targetDate.toISOString().split('T')[0] ?? '';
      const formatShort = `${targetDate.getDate().toString().padStart(2, '0')}/${(targetDate.getMonth() + 1).toString().padStart(2, '0')}`;

      let dayLabel = '';
      if (dayIndex === 0) dayLabel = 'Hoje';
      else if (dayIndex === 1) dayLabel = 'Amanhã';
      else if (dayIndex === 2) dayLabel = 'Depois';
      else {
        dayLabel = weekDayNames[targetDate.getDay()] ?? `Dia ${dayIndex + 1}`;
      }

      const matchedDateEntry = realCalendar?.dates?.find((d: any) => d.date === targetDateKey);

      let availableSlots: string[] = [];
      if (matchedDateEntry?.slots && Array.isArray(matchedDateEntry.slots)) {
        availableSlots = matchedDateEntry.slots
          .filter((s: any) => (s.status === 'AVAILABLE' || s.status === undefined || s.available) && !s.isBlocked)
          .map((s: any) => s.startTime);
      }

      const isWeekend = targetDate.getDay() === 0 || targetDate.getDay() === 6;
      if (availableSlots.length === 0 && !isWeekend && (!realCalendar || realCalendar.dates?.length === 0)) {
        availableSlots = ['08:00', '08:30', '09:00', '09:30', '14:00', '16:00'];
      }

      const isToday = targetDateKey === spTodayStr || dayIndex === 0;
      if (isToday) {
        availableSlots = availableSlots.filter((time) => time > nowSpTimeStr);

        if (isOnline) {
          availableSlots = ['Agora', ...availableSlots.filter((s) => s !== 'Agora').slice(0, 3)];
        } else {
          availableSlots = availableSlots.slice(0, 4);
        }
      } else {
        availableSlots = availableSlots.slice(0, 4);
      }

      days.push({
        dayLabel,
        dateStr: targetDateKey,
        formatShort,
        slots: availableSlots,
      });
    }

    return days;
  }, [dayOffset, realCalendar, spTodayStr, nowSpTimeStr, isOnline]);

  const initials = displayName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p: string) => p[0]?.toUpperCase())
    .join('');

  return (
    <div className="bg-card text-card-foreground rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-white/[0.08] shadow-xs sm:shadow-sm hover:shadow-md sm:hover:shadow-xl dark:hover:shadow-[0_16px_36px_rgba(0,0,0,0.5)] hover:border-blue-400 dark:hover:border-blue-500 transition-all duration-300 overflow-hidden group">
      <div className="p-4 sm:p-5 lg:p-6 grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-stretch">
        {/* LADO ESQUERDO: Perfil, Especialidade, CRM, Modalidades e Preço */}
        <div className="lg:col-span-7 flex flex-col justify-between space-y-4">
          <div>
            {/* Badges de Destaque */}
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <span className="px-2 sm:px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-blue-600/15 via-indigo-600/20 to-purple-600/15 text-blue-800 dark:text-blue-300 border border-blue-500/30 shadow-2xs flex items-center gap-1">
                <Sparkles className="w-2.5 h-2.5 text-blue-600 dark:text-blue-400" />
                <span>Destaque Parceiro</span>
              </span>
              <span className="px-2 sm:px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                Agenda Rápida
              </span>
              <span className="px-2 sm:px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider bg-blue-500/10 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400">
                Mais Escolhida
              </span>
              <span className="px-2 sm:px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10px] font-extrabold uppercase tracking-wider bg-purple-500/10 dark:bg-purple-500/20 text-purple-600 dark:text-purple-400">
                Especialista RQE
              </span>
            </div>

            {/* Identidade do Especialista */}
            <div className="flex items-start gap-3 sm:gap-4 lg:gap-5">
              <div className="relative shrink-0">
                {photoUrl && !imgError ? (
                  <img
                    src={photoUrl}
                    alt={displayName}
                    onError={() => setImgError(true)}
                    className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-2xl object-cover border-2 border-white dark:border-slate-800 shadow-md bg-slate-100 group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-2xl flex items-center justify-center font-black text-lg sm:text-xl shadow-md border-2 border-white dark:border-slate-800 bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
                    {initials || 'DR'}
                  </div>
                )}
                {isOnline && (
                  <div className="absolute -bottom-1 -right-1 bg-emerald-500 text-white text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full border-2 border-white dark:border-slate-800 shadow-xs flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
                    <span>Online</span>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                  <h3
                    onClick={() => onSelectDoctor(doctor)}
                    className="text-base sm:text-lg lg:text-xl font-black text-foreground hover:text-primary transition-colors truncate cursor-pointer"
                  >
                    {displayName}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-950/60 px-1.5 sm:px-2 py-0.5 rounded-md border border-blue-200/60 dark:border-blue-800/60">
                    <ShieldCheck className="w-3 sm:w-3.5 h-3 sm:h-3.5 text-blue-600 dark:text-blue-400" />
                    <span>CRM Ativo</span>
                  </span>
                </div>

                <p className="text-xs sm:text-sm font-semibold text-muted-foreground mt-0.5 truncate">
                  {specialty} · <span className="font-mono">{realCrm}</span>
                </p>

                {/* Avaliação em Estrelas */}
                <div className="flex items-center gap-1.5 mt-1.5 sm:mt-2">
                  <div className="flex items-center text-amber-400">
                    {[1, 2, 3, 4, 5].map((st) => (
                      <Star key={st} className="w-3 sm:w-3.5 h-3 sm:h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className="text-xs font-black text-foreground">{rating.toFixed(1)}</span>
                  <span className="text-[11px] sm:text-xs text-muted-foreground font-medium">({totalRatings} opiniões verificadas)</span>
                </div>

                {/* Frase / Bio */}
                <p className="text-xs text-muted-foreground mt-1.5 sm:mt-2 line-clamp-2 leading-relaxed">
                  {bio}
                </p>

                {/* Chips de Modalidades Atendidas */}
                <div className="mt-2.5 sm:mt-3 flex items-center gap-1.5 sm:gap-2 text-[11px] sm:text-xs flex-wrap font-medium">
                  <span className="flex items-center gap-1 text-cyan-600 dark:text-cyan-400 font-bold bg-cyan-50 dark:bg-cyan-950/40 px-2 py-0.5 rounded-md border border-cyan-200/50 dark:border-cyan-800/50">
                    <Video className="w-3.5 h-3.5" />
                    Telemedicina
                  </span>
                  <span className="flex items-center gap-1 text-slate-600 dark:text-slate-300 font-semibold bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-md border border-slate-200 dark:border-slate-700">
                    <Building className="w-3.5 h-3.5 text-slate-500" />
                    Consultório
                  </span>
                  <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-md border border-emerald-200/50 dark:border-emerald-800/50">
                    <Home className="w-3.5 h-3.5 text-emerald-500" />
                    Domiciliar
                  </span>
                  <span className="flex items-center gap-1 text-primary font-semibold">
                    <FileText className="w-3.5 h-3.5" />
                    Receita MeMed ICP-Brasil
                  </span>
                  {doctor.distanceKm != null && !isNaN(Number(doctor.distanceKm)) && (
                    <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.5 rounded-md border border-emerald-300">
                      <MapPin className="w-3.5 h-3.5" />
                      {Number(doctor.distanceKm).toFixed(1)} km
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Rodapé: Preço e Botão */}
          <div className="pt-3 sm:pt-4 border-t border-border/60 flex flex-wrap items-center justify-between gap-3">
            <div>
              <span className="text-[9px] sm:text-[10px] uppercase font-bold text-muted-foreground tracking-wider block">
                Consulta a partir de
              </span>
              <span className="font-black text-lg sm:text-xl text-primary font-display">
                {formattedPrice}
              </span>
            </div>

            <Button
              onClick={() => onSelectDoctor(doctor)}
              variant="outline"
              size="sm"
              className="rounded-xl font-bold text-xs h-9 sm:h-10 border-border hover:border-primary hover:text-primary transition-colors gap-1.5 cursor-pointer w-full sm:w-auto"
            >
              <span>Ver Horários e Agenda</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* LADO DIREITO: Mini Agenda Doctoralia com Horários Interativos (5 cols) */}
        <div className="lg:col-span-5 bg-slate-50/70 dark:bg-slate-900/50 p-3 sm:p-4 lg:p-5 rounded-2xl border border-slate-200/80 dark:border-white/10 flex flex-col justify-between space-y-3">
          <div>
            <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 dark:text-slate-300 uppercase tracking-wider mb-2.5">
              <span className="flex items-center gap-1.5 text-blue-700 dark:text-blue-400 font-extrabold">
                <Calendar className="w-3.5 h-3.5" />
                <span>Escolha o Melhor Horário</span>
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  disabled={dayOffset === 0}
                  onClick={() => setDayOffset((prev) => Math.max(0, prev - 3))}
                  className="p-1 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-card hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer shadow-2xs"
                  title="3 dias anteriores"
                  aria-label="Ver 3 dias anteriores"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setDayOffset((prev) => prev + 3)}
                  className="p-1 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-card hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 disabled:opacity-30 disabled:pointer-events-none transition-all cursor-pointer shadow-2xs"
                  title="Próximos 3 dias"
                  aria-label="Ver próximos 3 dias"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* 3 Colunas de Dias com Slots em 1 Clique */}
            <div className="grid grid-cols-3 gap-2">
              {threeDaysSchedule.map((day, idx) => (
                <div key={idx} className="space-y-1.5 text-center">
                  <div className="bg-white dark:bg-card rounded-lg p-1.5 mb-2 border border-slate-200 dark:border-slate-800 shadow-2xs">
                    <div className="text-[10px] font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wider">
                      {day.dayLabel}
                    </div>
                    <div className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                      {day.formatShort}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    {day.slots.length > 0 ? (
                      day.slots.map((slotTime, sIdx) => {
                        const isNow = slotTime === 'Agora';
                        return (
                          <button
                            key={sIdx}
                            type="button"
                            onClick={() => onSelectSlot(doctor, day.dateStr, slotTime, `slot-${slotTime.replace(':', '')}`)}
                            className={cn(
                              'w-full py-2 px-1 text-xs font-bold rounded-lg transition-all duration-150 shadow-2xs hover:shadow-md cursor-pointer hover:scale-105 active:scale-95 flex items-center justify-center gap-1.5 border',
                              isNow
                                ? 'bg-emerald-50/90 dark:bg-emerald-950/40 hover:bg-emerald-600 hover:text-white text-emerald-700 dark:text-emerald-300 border-emerald-300/80 dark:border-emerald-800 hover:border-emerald-600'
                                : 'bg-white dark:bg-card hover:bg-blue-600 hover:text-white border-blue-200 dark:border-blue-900 text-blue-700 dark:text-blue-300 hover:border-blue-600'
                            )}
                            title={`Agendar com ${displayName} em ${day.dayLabel} às ${slotTime}`}
                          >
                            {isNow && (
                              <span className="relative flex h-2 w-2 shrink-0">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                              </span>
                            )}
                            <span>{slotTime}</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="py-4 px-1 rounded-xl bg-slate-100/60 dark:bg-slate-800/40 border border-dashed border-slate-200 dark:border-slate-800 text-[10px] font-semibold text-muted-foreground flex items-center justify-center min-h-[72px]">
                        Sem vagas
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 flex items-center justify-between gap-2 border-t border-slate-200/60 dark:border-white/5">
            <span className="text-[11px] text-muted-foreground flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-emerald-500" />
              <span>Sem taxa de cancelamento</span>
            </span>
            <button
              type="button"
              onClick={() => onSelectDoctor(doctor)}
              className="text-[11px] font-bold text-primary hover:underline inline-flex items-center gap-0.5 cursor-pointer"
            >
              <span>Ver mais datas</span>
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
